import React from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Form } from "react-bootstrap";
import logger from "../../../utils/logging";
import { EventIO, Event, EventGuestStatistic } from "../../../models/Event";
import { formatDate } from "../../../utils/dateUtils";
import { FieldValidation } from "./useCreateWizard";
import { BaseValidatedFormGroup, ValidatedCombobox } from "./validation";

interface StepSelectEventProps {
    events: Event[] | null;
    selectedEvent: Event | null;
    eventGuestStatistic: EventGuestStatistic | null;
    onEventChange: (event: Event | null) => void;
    validation?: FieldValidation;
}

const eventTextFieldProvider = (item: unknown): string => {
    if (EventIO.is(item)) {
        return item.name + " (" + formatDate(item.startDate) + ")";
    }
    if (typeof item === "string") {
        return item;
    }
    if (item == null) {
        return "";
    }
    logger.error("unknown type for event item", item);
    return "[unknown]";
};

const formatEvent = (event: Event | string | null): React.ReactElement => {
    if (EventIO.is(event)) {
        const formattedDate = formatDate(event.startDate);
        return (
            <span>
                <strong>{event.name}</strong> ({formattedDate})
            </span>
        );
    }
    if (typeof event === "string") {
        return <>{event}</>;
    } else {
        return <>-</>;
    }
};

const idProvider = (event: unknown): string => {
    if (EventIO.is(event)) {
        return event.id;
    }
    if (typeof event === "string") {
        return event;
    }
    if (event == null) {
        return "<null>";
    }

    logger.error("unknown type for event item", event);
    return "[unknown]";
};

const StepSelectEvent: React.FC<StepSelectEventProps> = ({
    events,
    selectedEvent,
    eventGuestStatistic,
    onEventChange,
    validation,
}) => {
    const { t } = useTranslation("wizard");

    return (
        <div className="step-select-event">
            <h3 className="mb-4">{t("selectEvent.stepTitle")}</h3>
            <p className="text-muted small mb-4">{t("selectEvent.description")}</p>

            <Row>
                <Col md={8}>
                    <BaseValidatedFormGroup
                        className="form-group"
                        controlId="formGroupEvent_input"
                        validation={validation}
                    >
                        <Form.Label>{t("selectEvent.label")}</Form.Label>
                        <ValidatedCombobox<Event>
                            id="formGroupEvent"
                            busy={events == null}
                            data={events == null ? [] : events}
                            value={selectedEvent || undefined}
                            dataKey={(item) => idProvider(item)}
                            textField={eventTextFieldProvider}
                            renderListItem={({ item }) => formatEvent(item)}
                            onChange={(value) => {
                                logger.debug("set new selected event:", value);
                                onEventChange(value || null);
                            }}
                            placeholder={t("selectEvent.placeholder")}
                            validation={validation}
                        />
                        <Form.Text className="text-muted">{t("selectEvent.helpText")}</Form.Text>
                    </BaseValidatedFormGroup>
                </Col>
            </Row>

            {eventGuestStatistic && eventGuestStatistic?.all?.count == 0 && (
                <Row className="mt-4">
                    <Col md={8}>
                        <div className="alert alert-warning">
                            <h5>{t("selectEvent.zeroGuestWarning.title")}</h5>
                            <p className="mb-1">{t("selectEvent.zeroGuestWarning.message")}</p>
                        </div>
                    </Col>
                </Row>
            )}

            {selectedEvent && (
                <Row className="mt-4">
                    <Col md={8}>
                        <div className="alert alert-info">
                            <h5>{t("selectEvent.selectedTitle")}</h5>
                            <p className="mb-1">
                                <strong>{t("selectEvent.selectedName")}</strong> {selectedEvent.name}
                            </p>
                            <p className="mb-1">
                                <strong>{t("selectEvent.selectedDate")}</strong> {formatDate(selectedEvent.startDate)}
                            </p>
                            {eventGuestStatistic?.all?.count != null && (
                                <p className="mb-1">
                                    <strong>{t("selectEvent.guestCount")}</strong> {eventGuestStatistic.all.count}
                                </p>
                            )}
                        </div>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default StepSelectEvent;
